import {Plural, Trans} from '@lingui/macro';
import {compact} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {intersperseNodes} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {fontSizes} from '../../../libs/shared/ui/src/styles/fontStyles';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {InboxModel} from '../../models/api/inboxModel';
import {RuleBundleModelStrict} from '../../models/api/ruleBundleModel';
import {fetchAsync} from '../../services/http/http';
import {getCompanyUrl} from '../../store/data/company/companySelectors';
import {useSettingsQuery} from '../helpers/useSettingsQuery';
import {InboxGraphInheritableProps} from './graph/inboxGraphTypes';
import {ZoomableInboxGraph} from './graph/zoomableInboxGraph';

/*
 * Props.
 */

interface InboxTrafficProps extends InboxGraphInheritableProps {
  inbox: InboxModel;
}

/*
 * Style.
 */

const StyledTrafficWrapper = styled.div`
  margin-top: 41px;
  padding-top: 20px;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledTrafficInformationList = styled.ul`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  line-height: 146%;
  margin-bottom: 15px;
  list-style-type: none;
  padding-left: 0;
`;

const StyledSubtitle = styled.div`
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.large};
  line-height: 19px;
  margin-bottom: 5px;
`;

/*
 * Component.
 */

export const InboxTraffic: FC<InboxTrafficProps> = ({inbox, ...inheritableProps}) => {
  const companyUrl = useAppSelector(getCompanyUrl);

  const {data, asyncStatus} = useSettingsQuery(['impactingBundles', {inboxId: inbox.id}], async () => {
    const res = await fetchAsync(`${companyUrl}/inboxes/${inbox.id}/rule_bundles`);

    return {
      move_in: res.move_in as ReadonlyArray<RuleBundleModelStrict>,
      move_out: res.move_out as ReadonlyArray<RuleBundleModelStrict>,
      assign: res.assign as ReadonlyArray<RuleBundleModelStrict>,
    };
  });

  const {channels} = inheritableProps;
  const nbChannels = channels.length;
  const nbMoveInBundles = data?.move_in.length ?? 0;
  const nbMoveOutBundles = data?.move_out.length ?? 0;

  return (
    <StyledTrafficWrapper>
      <StyledSubtitle>
        <Trans id="inboxtraffic-title">Traffic</Trans>
      </StyledSubtitle>
      <StyledTrafficInformationList>
        {nbChannels + nbMoveInBundles > 0 && (
          <li>
            <Trans id="inboxtraffic-movinginto">Moving into inbox:</Trans>{' '}
            {intersperseNodes(
              compact([
                nbChannels > 0
                  ? {
                      node: (
                        <Plural
                          id="settingsinboxtraffic-nbchannels"
                          value={nbChannels}
                          one="1 channel"
                          other="# channels"
                        />
                      ),
                      key: 'channels',
                    }
                  : undefined,
                nbMoveInBundles > 0
                  ? {
                      node: (
                        <Plural
                          id="settingsinboxtraffic-nbrules"
                          value={nbMoveInBundles}
                          one="1 rule"
                          other="# rules"
                        />
                      ),
                      key: 'rules',
                    }
                  : undefined,
              ]),
              () => ', ',
            )}
          </li>
        )}
        {nbMoveOutBundles > 0 && (
          <li>
            <Plural
              id="settingsinboxtraffic-outnbrules"
              value={nbMoveOutBundles}
              one="Moving out of inbox: 1 rule"
              other="Moving out of inbox: # rules"
            />
          </li>
        )}
        {nbChannels + nbMoveInBundles + nbMoveOutBundles === 0 && (
          <li>
            <Trans id="inboxtraffic-nochannelsnorules">
              No channels, no moving rules. You can manually move messages to this inbox.
            </Trans>
          </li>
        )}
      </StyledTrafficInformationList>
      {asyncStatus === AsyncStatusesEnum.LOADED && data?.move_in && data?.move_out && (
        <ZoomableInboxGraph
          inbox={inbox}
          moveInBundles={data?.move_in}
          moveOutBundles={data?.move_out}
          {...inheritableProps}
          isExpandable={nbChannels + nbMoveInBundles + nbMoveOutBundles > 0}
        />
      )}
    </StyledTrafficWrapper>
  );
};
